import { useCallback, useMemo } from 'react';
import { InputNumber, Button, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { neutral } from '@/theme';

const { Text } = Typography;

interface KeyValueMapEditorProps {
  value: string;
  onChange: (json: string) => void;
  knownKeys: Record<string, string>;
  disabled?: boolean;
}

function parseMap(val: string): Record<string, number> {
  try {
    const parsed = JSON.parse(val);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, number>;
    }
  } catch {
    // ignore
  }
  return {};
}

export function KeyValueMapEditor({
  value,
  onChange,
  knownKeys,
  disabled = false,
}: KeyValueMapEditorProps) {
  const map = useMemo(() => parseMap(value), [value]);

  const emitChange = useCallback(
    (next: Record<string, number>) => {
      onChange(JSON.stringify(next));
    },
    [onChange],
  );

  const handleValueChange = useCallback(
    (key: string, numVal: number | null) => {
      const next = { ...map };
      if (numVal === null || numVal === undefined) {
        delete next[key];
      } else {
        next[key] = numVal;
      }
      emitChange(next);
    },
    [map, emitChange],
  );

  const handleDelete = useCallback(
    (key: string) => {
      const next = { ...map };
      delete next[key];
      emitChange(next);
    },
    [map, emitChange],
  );

  const handleAdd = useCallback(
    (key: string) => {
      const next = { ...map, [key]: 0 };
      emitChange(next);
    },
    [map, emitChange],
  );

  const entries = Object.entries(map);
  const availableKeys = Object.keys(knownKeys).filter((k) => !(k in map));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
      {entries.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Aucune entrée définie.
        </Text>
      )}
      {entries.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            border: `1px solid ${neutral[100]}`,
            borderRadius: 8,
          }}
        >
          <Text
            style={{
              minWidth: 180,
              fontSize: 13,
              fontFamily: 'monospace',
            }}
          >
            {knownKeys[k] ?? k}
          </Text>
          <InputNumber
            value={v}
            onChange={(n) => handleValueChange(k, n)}
            min={0}
            disabled={disabled}
            controls={false}
            style={{ width: 100 }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            pts
          </Text>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            disabled={disabled}
            onClick={() => handleDelete(k)}
          />
        </div>
      ))}
      {availableKeys.length > 0 && (
        <Space>
          <Select
            placeholder="Ajouter un type"
            style={{ minWidth: 200 }}
            options={availableKeys.map((k) => ({
              value: k,
              label: knownKeys[k] ?? k,
            }))}
            onChange={handleAdd}
            disabled={disabled}
            value={undefined}
          />
          <Button
            icon={<PlusOutlined />}
            size="small"
            disabled={disabled}
            onClick={() => {
              if (availableKeys.length > 0) {
                handleAdd(availableKeys[0]!);
              }
            }}
          >
            Ajouter
          </Button>
        </Space>
      )}
    </div>
  );
}
